import { gql } from "@apollo/client";
import ComponentState, { ComponentStateTypes } from "components/common/ComponentState";
import Form from "components/common/form/Form";
import CheckboxInput from "components/common/form/inputs/CheckboxInput";
import Input from "components/common/form/inputs/Input";
import Textarea from "components/common/form/inputs/Textarea";
import DateInput from "components/common/form/inputs/date/DateInput";
import FormActions from "components/common/form/partials/FormActions";
import ModalPortal from "components/modal/ModalPortal";
import { graph } from "config/axios-context";
import { useEffect, useState } from "react";
import { BookingAdditionalChargeInput, BookingAdditionalChargesFormDataQuery, BookingAdditionalChargesFormDataQueryVariables, SaveBookingAdditionalChargesMutation, SaveBookingAdditionalChargesMutationVariables } from "types/graphql";
import { DateUtil } from "utilities/date-utilities";
import useAppContext from "utilities/hooks/useAppContext";

export const BOOKING_ADDITIONAL_CHARGES_DATALIST_FORM = "-booking-additional-charges-datalist-form"

type BookingAdditionalChargesFormProps = {
    model?: Model | string | number,
    bookingId: number | string,
    name: string,
    onSave?: (id: number) => void,
    asModal?: boolean,
    onReset?: () => void
}

type Model = {
    name: string,
    note?: string,
    amount: number | string,
    date?: Date,
    id: number,
    isIncome: boolean
}

const query = gql`
query BookingAdditionalChargesFormData($id: Int) {
    bookingAdditionalCharge(id: $id) {
      bookingId
      amount
      date
      name
      id
      note
      isIncome
    }
  }  
`;

const mutation = gql`
mutation SaveBookingAdditionalCharges($model: BookingAdditionalChargeInput!) {
    bookingAdditionalCharge(model: $model) {
        id
    }
}
`;

const BookingAdditionalChargesForm = (props: BookingAdditionalChargesFormProps) => {
    const context = useAppContext();
    const [disableInputs, setDisableInputs] = useState(false);
    const [componentState, setComponentState] = useState<ComponentStateTypes>('');
    const [modalId, setModalId] = useState<string>(props.name + BOOKING_ADDITIONAL_CHARGES_DATALIST_FORM);
    const [model, setModel] = useState<Model>({
        amount: '',
        date: new Date(),
        name: '',
        note: '',
        id: 0,
        isIncome: false
    });

    useEffect(() => {
        if (props.model) {
            if (typeof props.model === 'string' || typeof props.model === 'number') {
                load();
            } else if(typeof props.model === 'object') {
                setModel({
                    amount: props.model.amount,
                    date: props.model.date ? new Date(props.model.date) : new Date(),
                    name: props.model.name,
                    note: props.model.note,
                    id: props.model.id || 0,
                    isIncome: props.model.isIncome
                });
            }
        }
    }, [props.model]);

    useEffect(() => {
        setModalId(props.name + BOOKING_ADDITIONAL_CHARGES_DATALIST_FORM);
    }, [props.model]);

    const load = async () => {
        setComponentState('loading');
        try {
            const response = await graph<BookingAdditionalChargesFormDataQuery, BookingAdditionalChargesFormDataQueryVariables>({
                query,
                variables: {
                    id: Number(props.model)
                }
            });

            if (response.errors?.length) {
                throw response.errors;
            }

            setModel({
                amount: response.data?.bookingAdditionalCharge?.amount || '',
                date: response.data?.bookingAdditionalCharge?.date ? new Date(response.data?.bookingAdditionalCharge?.date) : new Date(),
                name: response.data?.bookingAdditionalCharge?.name || '',
                note: response.data?.bookingAdditionalCharge?.note || '',
                id: response.data?.bookingAdditionalCharge?.id || 0,
                isIncome: response.data?.bookingAdditionalCharge?.isIncome || false
            });
            setComponentState('');
        } catch (error: any) {
            context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
            setComponentState('error');
            throw error;
        }
    }

    const onSubmit = async () => {
        setDisableInputs(true);
        const input: BookingAdditionalChargeInput = {
            name: model.name,
            amount: Number(model.amount),
            date: DateUtil.toIsoDate(model.date!),
            note: model.note,
            bookingId: Number(props.bookingId),
            id: model.id,
            isIncome: model.isIncome ? true : false
        }

        try {
            const response = await graph<SaveBookingAdditionalChargesMutation, SaveBookingAdditionalChargesMutationVariables>({
                query: mutation,
                variables: {
                    model: input
                }
            });

            if (response.errors?.length) {
                throw response.errors;
            }

            context.notify('success', 'request.save.success');
            if (props.onSave) {
                props.onSave(response.data?.bookingAdditionalCharge?.id || 0);
            } else {
                context.navigate(-1, undefined, {
                    replace: true
                })
            }
        } catch (error: any) {
            context.notify('error', 'request.save.error', Array.isArray(error) ? error[0].message : error.message);
            throw error;
        } finally {
            setDisableInputs(false);
        }
    }

    const view = () => {
        return (
            <ComponentState state={componentState}>
                <Form name={`${props.name}-booking-additional-charges-form`} onSubmit={onSubmit} onReset={props.onReset}>
                    <fieldset>
                        <Input
                            name="name"
                            label="fields.name.name"
                            required
                            value={model?.name}
                            onChange={(e) => setModel({ ...model, name: e || '' })}
                        />
                        <DateInput
                            name="date"
                            label="fields.date.name"
                            required
                            value={model?.date}
                            onChange={(e) => setModel({ ...model, date: e })}
                        />
                        <Input
                            name="amount"
                            label="fields.amount.name"
                            required
                            type="number"
                            value={model?.amount?.toString()}
                            onChange={(e) => setModel({ ...model, amount: e || '' })}
                        />
                        <CheckboxInput
                            name="isIncome"
                            label="fields.isIncome.name"
                            value={model?.isIncome ?? false}
                            onChange={v => setModel({ ...model, isIncome: v || false })}
                        />
                        <Textarea
                            name="note"
                            label="fields.note.name"
                            value={model?.note}
                            onChange={(e) => setModel({ ...model, note: e })}
                        />
                    </fieldset>
                    <FormActions disableInputs={disableInputs} />
                </Form>
            </ComponentState>
        );
    }

    return !props.asModal ? view() : (
        <ModalPortal name={modalId} title="pages.accommodation.additionalCharges.form">
            {view()}
        </ModalPortal>
    );
}

export default BookingAdditionalChargesForm;
